import TomSelect from '../tom-select.min';

import { currencyStorage } from '../Utils/currencyStorage';
import {
    CURRENCY_SERVICE_INITIALIZED,
    CURRENCY_CHANGED_EVENT,
    DIRECT_CHECKOUT_WIDGET_CONFIG_CHANGED_EVENT
} from '../events';

import { fetchWpJson } from '../api';
import { CurrencyInfo } from '../Utils/currencyInfo';

const getCurrencyBodyClass = (currencyIsoCode) => {
    return 'currency-' + currencyIsoCode.toLowerCase()
}

const addCurrencyClassToBody = (currencyIsoCode) => {
    document.body.classList.add(
        getCurrencyBodyClass(currencyIsoCode)
    );
}

const removeCurrencyClassFromBody = (currencyIsoCode) => {
    document.body.classList.remove(
        getCurrencyBodyClass(currencyIsoCode)
    );
}

const fetchDefaultCurrency = async () => {
    try {
        /**
         * POST method is intentionally used here to ensure reliability and prevent any potential caching issues across different network layers.
         * Even with No-Cache headers added to a GET request, caching may still happen across proxies, CDNs, or browsers.
         */
        const response = await fetchWpJson('/get-default-currency', {
            method: 'POST'
        });

        return CurrencyInfo.fromJson(
            await response.json()
        );
    } catch (error) {
        console.error('Error fetching default currency', error);
        return null;
    }
}

const updateDirectCheckoutCurrency = (currencyIsoCode) => {
    if (!currencyIsoCode) {
        return;
    }

    window.directCheckoutWidgetConfig.currency = currencyIsoCode.toUpperCase();

    document.dispatchEvent(
        new Event(DIRECT_CHECKOUT_WIDGET_CONFIG_CHANGED_EVENT)
    );
}

const dispatchDetailedCurrencyEvent = (eventName, currencyInfo) => {
    const detailedCustomEvent = new CustomEvent(eventName, {
        detail: { currencyInfo }
    });

    document.dispatchEvent(detailedCustomEvent);
}

const extractCurrencyOptionsFromElement = (element) => {
    if (element == null) {
        return [];
    }
    return Array.from(element.options).reduce((accumulator, currentOption) => {
        accumulator.push(currentOption.value);
        return accumulator;
    }, []);
}

const updateCurrency = (currencyIsoCode, currencyOptions) => {
    const currencyInfo = CurrencyInfo.fromCode(currencyIsoCode);

    currencyOptions.forEach(option => {
        if (option === currencyIsoCode) {
            return;
        }

        removeCurrencyClassFromBody(option);
    });

    addCurrencyClassToBody(currencyIsoCode);
    dispatchDetailedCurrencyEvent(CURRENCY_CHANGED_EVENT, currencyInfo);
    currencyStorage.setSelectedCurrency(currencyInfo.code, currencyInfo.symbol);
    updateDirectCheckoutCurrency(currencyIsoCode);
}

const getCurrencySwitcherElements = () => {
    return document.querySelectorAll('.currency-switcher-select');
}

const initCurrencySwitcherElements = (preselectedCurrencyIsoCode) => {
    const elements = getCurrencySwitcherElements();

    let updatingCurrencySelector = false;

    elements.forEach(element => {
        element.value = preselectedCurrencyIsoCode;

        element.tomSelect = new TomSelect(element, {
            create: false
        });

        element.tomSelect.on('change', (selectedCurrencyIsoCode) => {
            if (updatingCurrencySelector) {
                return;
            }

            updatingCurrencySelector = true;

            try {
                const currencyOptions = extractCurrencyOptionsFromElement(element);
                updateCurrency(selectedCurrencyIsoCode, currencyOptions);
            } finally {
                updatingCurrencySelector = false;
            }
        });
    });
};

const currencySwitcherService = async () => {
    let currencyInfo = currencyStorage.getSelectedCurrency();
    const currencyElement = getCurrencySwitcherElements()[0];
    const currencyOptions = extractCurrencyOptionsFromElement(currencyElement);

    if (currencyInfo == null) {
        const fetchedDefaultCurrency = await fetchDefaultCurrency();

        if (fetchedDefaultCurrency) {
            currencyInfo = fetchedDefaultCurrency;
        } else {
            currencyInfo = currencyStorage.getDefaultCurrency();
        }

        currencyStorage.setSelectedCurrency(currencyInfo.code, currencyInfo.symbol);
    }

    updateCurrency(currencyInfo.code, currencyOptions)
    initCurrencySwitcherElements(currencyInfo.code);

    dispatchDetailedCurrencyEvent(CURRENCY_SERVICE_INITIALIZED, currencyInfo);
}

export default currencySwitcherService;
